import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Preferences } from '@capacitor/preferences';
import { AlertController, ModalController } from '@ionic/angular';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { ToastService } from '../../_core/services/toast.service';
import { CountriesPage } from '../../countries/countries.page';
import { AuthService } from '../../auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, from } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { UserService } from '../../_core/services/user.service';
import { VerifyOtpPopupComponent } from '../verify-otp-popup/verify-otp-popup.component';
interface LoginForm {
    country_code: FormControl<string | null>;
    phone: FormControl<string | null>;
    password: FormControl<string | null>;
}

@Component({
    standalone: true, 
    selector: 'app-login',
    imports: [
        ReactiveFormsModule,
        IonicModule,
        CommonModule,
        TranslateModule,
        RouterModule
    ],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
    form = new FormGroup<LoginForm>({
        country_code: new FormControl('+1', [Validators.required, Validators.pattern('([+]{1})([0-9]{1,3})')]),
        phone: new FormControl(null, [Validators.required, Validators.pattern('([1-9]{1})[0-9]{5,15}')]),
        password: new FormControl(null, [Validators.required, Validators.minLength(6)])
    })

    isLogin = false;
    isVerifyingOtp = false;
    attemptedSubmit = false;
    isLoading = false;
    isModal = false;
    loginUrl = '';

    private readonly destroy$: Subject<void> = new Subject();

    constructor(
        private userService: UserService,
        private authService: AuthService,
        private modalCtrl: ModalController,
        private toastService: ToastService,
        private activatedRoute: ActivatedRoute,
        private alertCtrl: AlertController,
        private router: Router
    ) { }

    async ngOnInit() {
        this.loginUrl = this.activatedRoute.snapshot.queryParamMap.get('returnTo') || '/';
        this.removeTokens();

        if (await this.modalCtrl.getTop()) this.isModal = true;
    }

    async ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private removeTokens() {

        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
            this.authService.removeRefreshToken(refreshToken)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: async () => {
                        localStorage.removeItem('refreshToken');
                        const accessToken = localStorage.getItem('token');
                        if (accessToken) {
                            localStorage.removeItem('token');
                            localStorage.removeItem('accessToken');
                            localStorage.removeItem('refreshToken');
                            localStorage.removeItem('cognitoUsername');
                        }

                        const authUserPref = await Preferences.get({ key: 'authUser' });
                        if (authUserPref && authUserPref.value) {
                            await Preferences.remove({ key: 'authUser' });
                        }
                    }
                })
        }
    }

    private clearPendingData() {
        localStorage.removeItem('pendingEmail');
        localStorage.removeItem('pendingPhone');
        localStorage.removeItem('tempPasswordForOTP');
        localStorage.removeItem('pendingCountryCode');
    }

     private async checkIfSameUser(loggedInUser: any): Promise<boolean> {
        const pendingEmail = localStorage.getItem('pendingEmail');
        const pendingPhone = localStorage.getItem('pendingPhone');
        
        if (!pendingEmail) return false;

        const userPhone = `${loggedInUser.country_code || ''}${loggedInUser.phone || ''}`;
        const pendingFullPhone = pendingPhone;
        
        return userPhone === pendingFullPhone;
    }

    private async sendOTPEmail(email: string, phone: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.userService.sendOTPEmail(email, phone).subscribe(
                async (response) => {
                    this.toastService.presentToast('OTP sent to your email');
                    resolve(true);
                },
                (error) => {
                    console.error('Error sending OTP:', error);
                    this.toastService.presentDangerToast('Failed to send OTP. Please try again.');
                    resolve(false);
                }
            );
        });
    }

    private async showOTPVerificationAlert(email: string, phone: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            const modal = await this.modalCtrl.create({
                component: VerifyOtpPopupComponent,
                backdropDismiss: false,
                componentProps: {
                    email: email,
                    phone: phone
                },
                presentingElement: document.querySelector('ion-router-outlet')
            });

            await modal.present();

            const { data, role } = await modal.onDidDismiss();

            if (role === 'cancel') {
                resolve(false);
                this.toastService.presentDangerToast('Email verification cancelled');
            } else if (data?.success) {
                const { code } = data;
                let otp = code.toString()   
                const verified = await this.verifyOTP(email, otp, phone, );
                resolve(verified);
            } else {
                resolve(false);
            }
        });
    }

    private async verifyOTP(email: string, otp: string, phone: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.isVerifyingOtp = true;
            this.toastService.presentToast('Verifying OTP...', 'dark', 5000);
            const password = localStorage.getItem('tempPasswordForOTP') || ""; 
            this.userService.verifyOTP(email, otp, phone, password).subscribe(
                async (response) => {
                    this.toastService.presentToast('Email verified successfully');
                    this.isVerifyingOtp = false;
                    resolve(true);
                },
                async (error) => {
                    console.error('Error verifying OTP:', error);
                    this.isVerifyingOtp = false;
                    if (error.status === 500) {
                        this.toastService.presentDangerToast('Internal Server Error.');
                    } else {
                        this.toastService.presentDangerToast('Invalid OTP. Please try again.');
                        resolve(false);
                    }
                }
            );
        });
    }

    markFieldsDirty() {
        const controls = this.form.controls;
        for (const field in controls) {
            if (controls[field]) {
                controls[field].markAsDirty();
            }
        }
    }

    async login() {
        this.attemptedSubmit = true;
        if (this.form.valid) {
            this.isLoading = true;
            const rawValue = this.form.getRawValue();
            const credentials = {
                country_code: rawValue.country_code ?? '',
                phone: rawValue.phone ?? '',
                password: rawValue.password ?? ''
            };
            (await this.authService.login(credentials))
                .pipe(
                    finalize(() => this.isLoading = false),
                    switchMap((res) => {
                        return from((async () => {
                            await this.authService.getAuthUser();
                            this.authService.refresh$.next(null);
                            await Preferences.set({ key: 'authUser', value: JSON.stringify(res.user) });
                            this.authService.publishLoggedIn(true);

                            const pendingEmail = localStorage.getItem('pendingEmail') || "";
                            const pendingPhone = localStorage.getItem('pendingPhone') || "";

                            const modal = await this.modalCtrl.getTop();
                            if (modal) {
                                await modal.dismiss(res.user);
                                return res;
                            }

                            if (pendingEmail) {
                                const isSameUser = await this.checkIfSameUser(res.user);

                                if (!isSameUser) {
                                    this.clearPendingData();
                                    await this.authService.getAuthUser();
                                    this.authService.refresh$.next(null);
                                    await this.router.navigateByUrl(this.loginUrl);
                                    window.location.reload()
                                    return res;
                                }

                                const password = this.form.get('password')?.value || "";
                                localStorage.setItem('tempPasswordForOTP', password);

                                const otpSent = await this.sendOTPEmail(pendingEmail, pendingPhone);

                                if (otpSent) {
                                    const verified = await this.showOTPVerificationAlert(pendingEmail, pendingPhone);

                                    if (verified) {
                                        this.clearPendingData();

                                        const updatedUser = await this.authService.getAuthUser();
                                        await Preferences.set({ key: 'authUser', value: JSON.stringify(updatedUser) });
                                        this.authService.refresh$.next(updatedUser);

                                        this.toastService.presentToast('Profile updated successfully');
                                        await this.router.navigate(['/my-profile'], { replaceUrl: true });
                                    } else {
                                        this.clearPendingData();
                                        await this.router.navigate(['/my-profile'], { replaceUrl: true });
                                    }
                                } else {
                                    this.clearPendingData();
                                    this.toastService.presentDangerToast('Unable to update email');
                                    await this.router.navigate(['/my-profile'], { replaceUrl: true });
                                }
                            } else {
                                await this.router.navigateByUrl(this.loginUrl);
                            }

                            return res;
                        })());
                    })
                )
                .subscribe({
                    next: async (res) => {
                        if (!res || res.skipSubscribeNav) return;
                    },
                    error: (error) => {
                        console.error('Login error:', error);
                        this.toastService.presentDangerToast('Login failed. Please check your credentials.');
                    }
                });
        } else {
            this.handleErrors();
        }
    }

    async handleErrors() {
        let errorMessage = '';
        if (this.form.get('country_code')?.invalid) {
            errorMessage = 'Country code must start with + followed by 1 to 3 digits';
        } else if (this.form.get('phone')?.invalid) {
            errorMessage = 'Phone must include area code and phone number. Digits only. If your first digit is 0 remove it.';
        } else {
            errorMessage = 'Password must have minimum 6 characters.';
        }
        this.toastService.presentDangerToast(errorMessage);
    }

    async getCountryCode() {
        const modal = await this.modalCtrl.create({
            component: CountriesPage
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data) {
            this.form.get('country_code')?.setValue(data);
        }
    }
}
